import { apiPost, httpClient } from '../client';
import { useAuthStore } from '../../auth/authStore';
import { LONG_TIMEOUT_MS } from '../../config/env';
import type {
  AdditionalServiceOfferRequest,
  AdditionalServiceOfferResponse,
  AdditionalServiceSelectRequest,
  AirCancelReservationRequest,
  AirCancelReservationResponse,
  AirModifyRequest,
  AirRePriceRequest,
  AirSearchRequest,
  AirSearchResponse,
  AirShoppingCartResponse,
  AirportListItem,
  AirportOption,
  AllocateRequest,
  BookingRequest,
  GetAirportByCityIDRequest,
  GetWithQueryRequest,
  OptionDetailsRequest,
  PaymentRequest,
  PaymentResponse,
  PrebookingRequest,
  RemoveAdditionalServiceRequest,
  UpdatePassengersRequest,
} from '../types/air';

/* ---------------------------------------------------------------------------
 * Airport lookup
 * ------------------------------------------------------------------------- */

/**
 * `Airport/GetWithQuery` returns a bare array (each item carries its own
 * `hasError: false`, no top-level ResponseBase wrapper) — confirmed via curl, see
 * api/types/air.ts file header. Can't go through `apiPost` (which requires
 * `TRes extends ResponseBase`), so this calls `httpClient` directly, replicating
 * apiPost's session-injection.
 */
export async function searchAirports(query: string): Promise<AirportOption[]> {
  const session = useAuthStore.getState().session;
  const body: GetWithQueryRequest & { sessionHeader?: typeof session } = {
    query,
    sessionHeader: session ?? undefined,
  };
  const response = await httpClient.post<AirportOption[]>('Airport/GetWithQuery', body);
  return response.data;
}

/**
 * `Airport/Get` — full airport dump (thousands of rows, snake_case keys, see
 * api/types/air.ts file header). Response is also a bare array (no ResponseBase
 * wrapper), same as GetWithQuery. Wired as the offline full-list fallback in
 * `AirportAutocomplete` — when `Airport/GetWithQuery` errors, the picker client-filters
 * this list instead.
 */
export async function getAllAirports(): Promise<AirportListItem[]> {
  const session = useAuthStore.getState().session;
  const response = await httpClient.post<AirportListItem[]>('Airport/Get', session ?? {});
  return response.data;
}

/**
 * `Airport/GetAirportsAirportByCityID` — given an airport code, the other airports
 * (`is_city == false`) sharing its `city_code`. Response is a raw `List<Airports_tr>` (no
 * ResponseBase wrapper), so we cast the same way `searchAirports` does. Wired into
 * `AirportAutocomplete` to list a picked city's member airports.
 */
export async function getAirportsByCityId(airportCode: string): Promise<AirportListItem[]> {
  const response = await apiPost<GetAirportByCityIDRequest, AirShoppingCartResponse>(
    'Airport/GetAirportsAirportByCityID',
    { airportCode }
  );
  return (response as unknown as AirportListItem[]) ?? [];
}

/* ---------------------------------------------------------------------------
 * Search — confirmed working end-to-end via live curl (real inventory returned).
 * ------------------------------------------------------------------------- */

/**
 * Tek yön / gidiş-dönüş / **çok noktalı** — hepsi tek `Air/Search` çağrısı. Çok
 * noktalıda istek `flightType: 'MULTI'` + `airSearchSegments` bacak başına bir eleman
 * (`sequenceNo` 1..N) taşır; backend düz sonuçları bacak bazında gruplayıp
 * `AirSearchResponse.multiOriginDestinationOptions[bacakIndex]` altında döner (yurtiçi
 * uçuşlar SADECE burada gelir, `originDestinationOptions`'da değil).
 *
 * NOT: legacy'de bir de `Air/SearchMulti` (`List<AirSearchRequest>` gövdeli, bacak başına
 * ayrı istek) ucu var ama legacy web bunu KULLANMIYOR — `FlightController.Search` her
 * arama tipinde `CreateAirSearchRequest` + `POST /Air/Search` çağırıyor, `SearchMulti`
 * kod yolu tamamen yorum satırı. Bu yüzden burada da yalnız `Air/Search` kullanılır.
 */
export function searchAir(body: AirSearchRequest): Promise<AirSearchResponse> {
  return apiPost<AirSearchRequest, AirSearchResponse>('Air/Search', body);
}

/**
 * `Air/Search/Combinable` — verilen gidiş opsiyonları için tek-yön-birleştirilebilir
 * dönüş opsiyonlarını döndürür. İstek `AllocateRequest` (`allocateItems[].productId` =
 * seçilen gidiş `OriginDestinationOption.id`), cevap **`AirSearchResponse`**
 * (`originDestinationOptions`, `TotalFare`'e göre sıralı) — kod-izli
 * `Docs/api/02-air.md` §CombinableSearch. `CombinableAirSearch` sonuçları da
 * `OptionCollection.Create` ile cache'lenir → dönen id'ler doğrudan `Air/Allocate`'e
 * `AllocateItem.productId` olarak geçer.
 *
 * NOT: önceki tip `Promise<AirShoppingCartResponse>`'du — YANLIŞTI (doküman
 * `OriginDestinationOptions` dönüyor diyor), SA-2'de düzeltildi. Bu ortamda canlı uçuş
 * envanteri yok — istek/cevap şekli dokümandan, canlı doğrulanmadı.
 */
export function searchAirCombinable(body: AllocateRequest): Promise<AirSearchResponse> {
  return apiPost<AllocateRequest, AirSearchResponse>('Air/Search/Combinable', body);
}

/**
 * `Air/Upsell` — seçilen opsiyon(lar) için alternatif (genelde daha üst) markalı ücretleri
 * döndürür. İstek `AllocateRequest`, cevap `AirSearchResponse` (`originDestinationOptions`
 * + `SessionHeader` dolu). `employeeID` verilirse sunucu kurumsal `PolicyFilterUpsell`'i
 * uygular — istemci yalnız sonucu gösterir (`AirUpsellPanel`). Envanter yok — dokümana
 * göre yazıldı, canlı doğrulanmadı.
 */
export function getAirUpsell(body: AllocateRequest): Promise<AirSearchResponse> {
  return apiPost<AllocateRequest, AirSearchResponse>('Air/Upsell', body);
}

/* ---------------------------------------------------------------------------
 * Allocate — confirmed working end-to-end via live curl (real ShoppingCart + real
 * AirBooking created from real search-result productId/fareCode).
 * ------------------------------------------------------------------------- */

export function allocateAir(body: AllocateRequest): Promise<AirShoppingCartResponse> {
  return apiPost<AllocateRequest, AirShoppingCartResponse>('Air/Allocate', body);
}

export function allocateAirMultiCity(body: AllocateRequest): Promise<AirShoppingCartResponse> {
  return apiPost<AllocateRequest, AirShoppingCartResponse>('Air/AllocateMultiCity', body);
}

/* ---------------------------------------------------------------------------
 * Passengers
 * ------------------------------------------------------------------------- */

export function updateAirPassengers(body: UpdatePassengersRequest): Promise<AirShoppingCartResponse> {
  return apiPost<UpdatePassengersRequest, AirShoppingCartResponse>('Air/UpdatePassengers', body);
}

/* ---------------------------------------------------------------------------
 * Prebooking / Finalize (async — long timeout, see migration guide Bölüm 7.1)
 * ------------------------------------------------------------------------- */

export function makeAirPrebooking(body: PrebookingRequest): Promise<AirShoppingCartResponse> {
  return apiPost<PrebookingRequest, AirShoppingCartResponse>('Air/MakePrebooking', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

export function makeAirPrebookingMultiCity(body: PrebookingRequest): Promise<AirShoppingCartResponse> {
  return apiPost<PrebookingRequest, AirShoppingCartResponse>('Air/MakePrebookingMultiCity', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

export function finalizeAirShopping(body: BookingRequest): Promise<AirShoppingCartResponse> {
  return apiPost<BookingRequest, AirShoppingCartResponse>('Air/FinalizeShopping', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

export function finalizeAirShoppingMultiCity(body: BookingRequest): Promise<AirShoppingCartResponse> {
  return apiPost<BookingRequest, AirShoppingCartResponse>('Air/FinalizeShoppingMultiCity', body, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}

/* ---------------------------------------------------------------------------
 * RePrice — SA-3 wired it into `ReservationDetailPage` ("Yeniden Fiyatla", shown for a
 * still-held `Prebooked` reservation). Body is `{ bookingData: { locator }, shoppingCart }`
 * — `locator` is the PNR/BookingCode; empty → `InputLogicError` "Pnr kodu belirtilmemiş."
 * Response carries the re-priced `ShoppingCart`; the endpoint itself only NLogs (no
 * persistence), so the UI just shows the new totals for the operator to eyeball. Not yet
 * exercised with live inventory.
 * ------------------------------------------------------------------------- */

export function repriceAir(body: AirRePriceRequest): Promise<AirShoppingCartResponse> {
  return apiPost<AirRePriceRequest, AirShoppingCartResponse>('Air/RePrice', body);
}

/* ---------------------------------------------------------------------------
 * Cancellation — field names confirmed via curl.
 * ------------------------------------------------------------------------- */

export function cancelAirReservation(body: AirCancelReservationRequest): Promise<AirCancelReservationResponse> {
  return apiPost<AirCancelReservationRequest, AirCancelReservationResponse>('Air/CancelReservation', body);
}

/* ---------------------------------------------------------------------------
 * Option (hold/opsiyon) — NOT FOUND IN LIVE SWAGGER, see api/types/air.ts header.
 * Kept wired in case a future backend build adds this route; do not expect it to work.
 * ------------------------------------------------------------------------- */

export function setAirOption(body: OptionDetailsRequest): Promise<AirShoppingCartResponse> {
  return apiPost<OptionDetailsRequest, AirShoppingCartResponse>('Air/Option', body);
}

/* ---------------------------------------------------------------------------
 * THY-NDC seat selection — see api/types/air.ts header for the confirmation notes
 * (legacy `AdditionalServicesController` source).
 * ------------------------------------------------------------------------- */

export function getAirAdditionalServiceOffer(
  body: AdditionalServiceOfferRequest
): Promise<AdditionalServiceOfferResponse> {
  return apiPost<AdditionalServiceOfferRequest, AdditionalServiceOfferResponse>(
    'Air/GetAdditionalServiceOffer',
    body
  );
}

export function selectAirAdditionalService(body: AdditionalServiceSelectRequest): Promise<AirShoppingCartResponse> {
  return apiPost<AdditionalServiceSelectRequest, AirShoppingCartResponse>('Air/SelectAdditionalService', body);
}

export function removeAirAdditionalService(body: RemoveAdditionalServiceRequest): Promise<AirShoppingCartResponse> {
  return apiPost<RemoveAdditionalServiceRequest, AirShoppingCartResponse>('Air/RemoveAdditionalService', body);
}

/* ---------------------------------------------------------------------------
 * Refund / Exchange — `Air/Modify/{Refund,Exchange}` (Swagger-hidden, `AirModifyController`).
 * BACKEND BROKEN: `AirModifyWorker.Refund` throws `NotImplementedException`, and the
 * `Exchange` controller action calls `_worker.Refund(request)` (wrong worker method) which
 * also throws — both return HTTP 500 (code-traced `Docs/api/02-air.md` §AirModifyController;
 * docs/backend-requirements.md §F-12). SA-3 surfaces "İade" / "Değişim" buttons in
 * `TicketPostSaleActions` but keeps them **disabled** — these wrappers are never called
 * today. The generic `/Void` route stays available via `voidShoppingCart` in ./shoppingCart.
 * ------------------------------------------------------------------------- */

export function refundAirTicket(body: AirModifyRequest): Promise<AirShoppingCartResponse> {
  return apiPost<AirModifyRequest, AirShoppingCartResponse>('Air/Modify/Refund', body);
}

export function exchangeAirTicket(body: AirModifyRequest): Promise<AirShoppingCartResponse> {
  return apiPost<AirModifyRequest, AirShoppingCartResponse>('Air/Modify/Exchange', body);
}

/* ---------------------------------------------------------------------------
 * Payment (shared, not Air-prefixed, async — long timeout)
 * ------------------------------------------------------------------------- */

export function makeAirPayment(body: PaymentRequest): Promise<PaymentResponse> {
  return apiPost<PaymentRequest, PaymentResponse>('MakePayment', body, { timeoutMs: LONG_TIMEOUT_MS });
}

/* ---------------------------------------------------------------------------
 * NOTE: `Report/Reservations` and `Entry/Find` are NOT wrapped here — both have a
 * canonical shared home at api/endpoints/reports.ts (see that file for `entryFind`,
 * used by every product module's reservation-detail page, not just Air's).
 * ------------------------------------------------------------------------- */
